import { Action, Module, Mutation, VuexModule } from 'vuex-class-modules'
import { AxiosResponse } from 'axios'
import store from '@/store/index'
import { load, save } from '@/utils/localStorage'
import support from '@/services/support'
import {
  SupportDialogQuote,
  SupportDialogQuoteResponse,
  SupportDialogReservation,
  SupportDialogReservationResponse,
} from '@/models/dto/SupportDialog'

@Module({ generateMutationSetters: true })
class SupportModule extends VuexModule {
  _isOpen = false
  _reservationId: number = null
  _reservations: SupportDialogReservation[] = load('reservations') || []
  _quoteId: number = null
  _quotes: SupportDialogQuote[] = load('quotes') || []
  _ticketTypeKey: string = null

  /**
   * Whether the support modal is open or not.
   * @returns whether the support modal is open or not.
   */
  get isOpen(): boolean {
    return this._isOpen
  }

  /**
   * Returns the ticket type associated with the support modal.
   * @returns the ticket type associated with the support modal.
   */
  get ticketTypeKey(): string {
    return this._ticketTypeKey
  }

  /**
   * Returns the reservation ID associated with the support modal.
   * @returns the reservation ID currently associated with the support modal.
   */
  get reservationId(): number {
    return this._reservationId
  }

  /**
   * Returns the reservations associated with the support modal.
   * @returns the reservations currently associated with the support modal.
   */
  get reservations(): SupportDialogReservation[] {
    return this._reservations
  }

  /**
   * Returns the quote ID associated with the support modal.
   * @returns the quote ID currently associated with the support modal.
   */
  get quoteId(): number {
    return this._quoteId
  }

  /**
   * Returns the quotes associated with the support modal.
   * @returns the quotes currently associated with the support modal.
   */
  get quotes(): SupportDialogQuote[] {
    return this._quotes
  }

  /**
   * Updates the ticket type associated with the support modal.
   * @param ticketTypeKey - the new ticket type.
   */
  @Mutation
  setTicketTypeKey(ticketTypeKey: string) {
    this._ticketTypeKey = ticketTypeKey
  }

  /**
   * Updates the reservation ID associated with the support modal.
   * @param reservationId - the new reservation ID.
   */
  @Mutation
  setReservationId(reservationId: number) {
    this._reservationId = reservationId
  }

  /**
   * Updates the reservations associated with the support modal.
   * @param reservations - the new reservations.
   */
  @Mutation
  setReservations(reservations: SupportDialogReservation[]) {
    this._reservations = reservations
    save('reservations', reservations)
  }

  /**
   * Updates the quote ID associated with the support modal.
   * @param quoteId - the new quote ID.
   */
  @Mutation
  setQuoteId(quoteId: number) {
    this._quoteId = quoteId
  }

  /**
   * Updates the quotes associated with the support modal.
   * @param quotes - the new quotes.
   */
  @Mutation
  setQuotes(quotes: SupportDialogQuote[]) {
    this._quotes = quotes
    save('quotes', quotes)
  }

  /**
   * Opens the support modal and sets an optional reservation ID and an optional quote ID.
   *
   * @param modalContext - object containing context to associate with the support modal.
   */
  @Action
  open(modalContext: {
    reservationId?: number
    quoteId?: number
    ticketTypeKey?: string
  }): void {
    this._isOpen = true
    this._reservationId = modalContext?.reservationId
    this._quoteId = modalContext?.quoteId
    this._ticketTypeKey = modalContext?.ticketTypeKey
  }

  /**
   * Closes the support modal.
   */
  @Action
  close(): void {
    this._isOpen = false
    this._reservationId = null
    this._quoteId = null
    this._ticketTypeKey = null
  }

  /**
   * Fetches all support quotes and reservations.
   */
  @Action
  async fetchAll(): Promise<void> {
    await Promise.all([this.fetchQuotes(), this.fetchReservations()])
  }

  /**
   * Fetches all tableview quotes and sets them in the store.
   */
  @Action
  async fetchQuotes(): Promise<void> {
    try {
      const response: AxiosResponse<SupportDialogQuoteResponse> = await support.fetchQuotes()
      this.setQuotes(response.data.quotes)
    } catch (error) {
      console.error('Failed to fetch support quotes:', error)
      this.setQuotes([]) // Set empty array instead of null on error
    }
  }

  /**
   * Fetches all tableview reservations and sets them in the store.
   */
  @Action
  async fetchReservations(): Promise<void> {
    try {
      const response: AxiosResponse<SupportDialogReservationResponse> = await support.fetchReservations()
      this.setReservations(response.data.reservations)
    } catch (error) {
      console.error('Failed to fetch support reservations:', error)
      this.setReservations([]) // Set empty array instead of null on error
    }
  }

  /**
   * Clears all tableview quotes and reservations.
   */
  @Action
  clear(): void {
    this.setReservations(null)
    this.setQuotes(null)
  }
}

export default new SupportModule({ store, name: 'support' })
